import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useRef, useImperativeHandle, forwardRef } from "react";
import classNames from 'classnames';
import { UXPContext } from "@core/context/Context";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import { getInputSizeStyles, getInputStateClass } from './utils';
/**
 * @export
 * A checkbox component that can render boolean and intermediate states in multiple visual styles.
 *
 * @example
 * ```
 * <Checkbox
 *     checked={checked}
 *     onChange={(isChecked) => setChecked(isChecked)}
 *     label='Are you sure'
 * />
 * ```
 *
 * @example
 * ```
 * <Checkbox
 *     checked='intermediate'
 *     onChange={(isChecked) => setChecked(isChecked)}
 *     label='Partial selection'
 *     type="switch-box"
 * />
 * ```
 *
 * @category Inputs/Selection
 */
const Checkbox = forwardRef((props, ref) => {
    const context = useContext(UXPContext);
    const { checked, onChange, label, isValid, inputAttr, type = "default", className, readOnly, disabled, tabIndex = 0, labelStyles, minWidth, maxWidth, width, fullWidth } = props;
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => ({
        focus: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus(); },
    }));
    const isChecked = checked === true;
    const isIntermediate = checked === 'intermediate';
    const handleChange = (e) => {
        if (readOnly || disabled)
            return;
        // Intermediate state becomes true when clicked, otherwise toggle normally
        const newValue = isIntermediate ? true : e.target.checked;
        onChange(newValue);
    };
    const handleKeyDown = (e) => {
        var _a;
        if (readOnly || disabled)
            return;
        if (e.key === 'Enter' || e.key === 'Space') {
            e.preventDefault();
            const labelElement = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('label');
            labelElement === null || labelElement === void 0 ? void 0 : labelElement.click();
        }
    };
    const renderIcon = () => {
        if (type === 'switch-line' || type === 'switch-box')
            return _jsx("div", { className: "uxpcore_checkbox__switch_btn" });
        if (isIntermediate) {
            return _jsx("div", { className: "icon uxpcore_checkbox__icon-container", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'minus'], className: "uxpcore_checkbox__icon" }) });
        }
        if (isChecked) {
            return _jsx("div", { className: "icon uxpcore_checkbox__icon-container", children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'check'], className: "uxpcore_checkbox__icon" }) });
        }
        return null;
    };
    // Calculate size styles and state classes
    const sizeStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid });
    const containerClasses = classNames("uxp-form-checkbox", // Legacy
    "uxpcore_checkbox", // new
    className, {
        // Legacy classes
        "toggle-line": type === 'switch-line',
        "toggle-box": type === "switch-box",
        "valid": isValid,
        "invalid": isValid === false,
        // New classes
        "uxpcore_checkbox--switch-line": type === 'switch-line',
        "uxpcore_checkbox--switch-box": type === "switch-box",
        "uxpcore_checkbox--checked": isChecked,
        "uxpcore_checkbox--intermediate": isIntermediate,
        "uxpcore_checkbox--valid": isValid === true,
        "uxpcore_checkbox--invalid": isValid === false,
        "uxpcore_checkbox--disabled": disabled,
        "uxpcore_checkbox--readonly": readOnly,
    });
    const placeholderClasses = classNames("checkbox-placeholder", // Legacy
    "uxpcore_checkbox__placeholder", // New
    {
        "checked": isChecked || isIntermediate, // Legacy
        "uxpcore_checkbox__placeholder--checked": isChecked,
        "uxpcore_checkbox__placeholder--intermediate": isIntermediate,
    });
    return (_jsx("div", { className: classNames("uxpcore_input", "uxpcore_checkbox-wrapper", stateClass, { "uxpcore_input--inline": true }), style: sizeStyles, children: _jsx("div", { className: containerClasses, tabIndex: disabled ? -1 : tabIndex, onKeyDown: handleKeyDown, ref: inputRef, children: _jsxs("label", { className: "uxpcore_checkbox__label", children: [_jsx("input", Object.assign({ type: "checkbox", className: "uxpcore_checkbox__input", onChange: handleChange, checked: isChecked, disabled: disabled, readOnly: readOnly, ref: (input) => {
                            if (input) {
                                input.indeterminate = isIntermediate;
                            }
                        } }, inputAttr)), _jsx("div", { className: placeholderClasses, children: renderIcon() }), label && (_jsx("div", { className: "label uxpcore_checkbox__text", style: labelStyles, children: label }))] }) }) }));
});
Checkbox.displayName = 'Checkbox';
export default Checkbox;
